import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import { supabase } from '@/lib/supabase'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card'
import { ArrowLeft, Lock, CheckCircle, AlertCircle } from 'lucide-react'
import { useToast } from '@/contexts/ToastContext'

export const ChangePasswordPage: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { user } = useAuth()
  const { success, error: showError } = useToast()
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [showPasswordStrength, setShowPasswordStrength] = useState(false)

  const getPasswordStrength = (password: string) => {
    let strength = 0
    if (password.length >= 8) strength++
    if (password.length >= 12) strength++
    if (/[a-z]/.test(password)) strength++
    if (/[A-Z]/.test(password)) strength++
    if (/[0-9]/.test(password)) strength++
    if (/[^a-zA-Z0-9]/.test(password)) strength++
    
    if (strength <= 2) return { level: 'Weak', color: 'text-red-600', bgColor: 'bg-red-200' }
    if (strength <= 4) return { level: 'Medium', color: 'text-amber-600', bgColor: 'bg-amber-200' }
    return { level: 'Strong', color: 'text-green-600', bgColor: 'bg-green-200' }
  }

  const passwordStrength = getPasswordStrength(newPassword)

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault()

    if (newPassword !== confirmPassword) {
      showError('Passwords Mismatch', 'New passwords do not match')
      return
    }

    if (newPassword.length < 8) {
      showError('Password Too Short', 'Password must be at least 8 characters')
      return
    }

    if (newPassword === currentPassword) {
      showError('Same Password', 'New password must be different from current password')
      return
    }

    setLoading(true)
    try {
      // first verify current password by attempting to sign in
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: user?.email || '',
        password: currentPassword
      })

      if (signInError) {
        throw new Error('Current password is incorrect')
      }

      // update to new password
      const { error } = await supabase.auth.updateUser({
        password: newPassword
      })

      if (error) throw error

      success(t('notifications.success'), 'Password changed successfully!')
      
      // clear form
      setCurrentPassword('')
      setNewPassword('')
      setConfirmPassword('')
      
      // redirect to settings or dashboard
      setTimeout(() => {
        navigate('/settings')
      }, 1500)
    } catch (err: any) {
      console.error('Error changing password:', err)
      showError(t('errors.operationFailed'), err.message || 'Failed to change password')
    } finally {
      setLoading(false)
    }
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="h-16 w-16 text-amber-600 mx-auto mb-4" />
          <h2 className="text-2xl font-bold mb-2">Please Login</h2>
          <p className="text-slate-600 mb-4">You must be logged in to change your password</p>
          <Link to="/login">
            <Button>Go to Login</Button>
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-white dark:bg-slate-900 text-slate-900 dark:text-white">
      <div className="border-b border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800">
        <div className="px-6 py-4 flex items-center gap-4">
          <Button variant="secondary" size="sm" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-4 w-4" />
            </Button>
          <div>
            <h1 className="text-xl font-semibold flex items-center gap-2">
              <Lock className="h-6 w-6" />
              Change Password
            </h1>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
              Update your account password
            </p>
          </div>
        </div>
      </div>

      <div className="mx-auto px-4 sm:px-6 lg:px-8 py-8 max-w-2xl">
        <Card>
          <CardHeader>
            <CardTitle>Change Your Password</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleChangePassword} className="space-y-4">
              {/* current user info */}
              <div className="mb-6 p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
                <p className="text-sm text-slate-600 dark:text-slate-400">
                  Changing password for:
                </p>
                <p className="font-medium text-slate-900 dark:text-white">
                  {user.email}
                </p>
              </div>

              {/* current password */}
              <div>
                <label className="block text-sm font-medium mb-2">
                  Current Password
                </label>
                <Input
                  type="password"
                  placeholder="Enter your current password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  required
                  autoFocus
                />
              </div>

              {/* new password */}
              <div>
                <label className="block text-sm font-medium mb-2">
                  New Password
                </label>
                <Input
                  type="password"
                  placeholder="Enter new password"
                  value={newPassword}
                  onChange={(e) => {
                    setNewPassword(e.target.value)
                    setShowPasswordStrength(true)
                  }}
                  required
                />
                
                {/* password strength indicator */}
                {showPasswordStrength && newPassword && (
                  <div className="mt-2">
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-2 bg-slate-200 rounded-full overflow-hidden">
                        <div 
                          className={`h-full transition-all ${passwordStrength.bgColor}`}
                          style={{ 
                            width: passwordStrength.level === 'Weak' ? '33%' : 
                                   passwordStrength.level === 'Medium' ? '66%' : '100%' 
                          }}
                        />
                      </div>
                      <span className={`text-xs font-medium ${passwordStrength.color}`}>
                        {passwordStrength.level}
                      </span>
                    </div>
                    <p className="text-xs text-slate-500 mt-1">
                      Use a mix of letters, numbers, and symbols for a strong password
                    </p>
                  </div>
                )}
              </div>

              {/* confirm password */}
              <div>
                <label className="block text-sm font-medium mb-2">
                  Confirm New Password
                </label>
                <Input
                  type="password"
                  placeholder="Confirm new password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                />
              </div>

              {/* password match indicator */}
              {newPassword && confirmPassword && (
                <div className={`text-xs p-2 rounded ${
                  newPassword === confirmPassword 
                    ? 'bg-green-50 text-green-700' 
                    : 'bg-red-50 text-red-700'
                }`}>
                  {newPassword === confirmPassword ? (
                    <div className="flex items-center gap-1">
                      <CheckCircle className="h-3 w-3" />
                      Passwords match
                    </div>
                  ) : (
                    <div className="flex items-center gap-1">
                      <AlertCircle className="h-3 w-3" />
                      Passwords do not match
                    </div>
                  )}
                </div>
              )}

              <div className="pt-4 space-y-2">
                <Button
                  type="submit"
                  className="w-full"
                  disabled={loading || newPassword !== confirmPassword || !newPassword || !currentPassword}
                >
                  {loading ? 'Updating Password...' : 'Change Password'}
                </Button>
                
                <Button
                  type="button"
                  variant="outline"
                  className="w-full"
                  onClick={() => navigate('/settings')}
                >
                  {t('common.cancel')}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}

